import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currencyCtx";
import { fmt, formatPct } from "@/lib/currency";
import { toast } from "sonner";
import { ChevronLeft, Save, Send, Undo2, Crown } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";

const CHART = ["#112A22", "#245243", "#3C826A", "#72B59F", "#B89645"];

export default function AdminDistributionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { currency, rate } = useCurrency();
  const [dist, setDist] = useState(null);
  const [partners, setPartners] = useState([]);
  const [entries, setEntries] = useState({}); // partner_id -> {revenue, capital, tenure_override}
  const [netProfit, setNetProfit] = useState(0);
  const [year, setYear] = useState(2025);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [d, p] = await Promise.all([
      api.get(`/distributions/${id}`),
      api.get("/partners"),
    ]);
    setDist(d.data);
    setPartners(p.data);
    setNetProfit(d.data.net_profit);
    setYear(d.data.year);
    setTitle(d.data.title || "");
    const map = {};
    (d.data.rows || []).forEach((r) => {
      map[r.partner_id] = {
        revenue_contribution: r.revenue_contribution,
        capital_contribution: r.capital_contribution,
        tenure_years_override: null,
      };
    });
    setEntries(map);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const setEntry = (pid, key, value) => {
    setEntries((m) => ({
      ...m,
      [pid]: { ...(m[pid] || { revenue_contribution: 0, capital_contribution: 0 }), [key]: value === "" ? 0 : Number(value) },
    }));
  };

  const ensureEntries = useMemo(() => {
    // ensure every partner has an entry slot
    const out = { ...entries };
    partners.forEach((p) => {
      if (!out[p.partner_id]) out[p.partner_id] = { revenue_contribution: 0, capital_contribution: p.capital_contribution || 0 };
    });
    return out;
  }, [partners, entries]);

  const save = async (publish = false) => {
    setSaving(true);
    try {
      const payload = {
        year: Number(year),
        title,
        net_profit: Number(netProfit),
        entries: partners.map((p) => ({
          partner_id: p.partner_id,
          revenue_contribution: Number(ensureEntries[p.partner_id]?.revenue_contribution || 0),
          capital_contribution: Number(ensureEntries[p.partner_id]?.capital_contribution || 0),
          tenure_years_override: null,
        })),
      };
      await api.put(`/distributions/${id}`, payload);
      if (publish) {
        await api.post(`/distributions/${id}/publish`);
        toast.success("Distribution published");
      } else {
        toast.success("Saved & simulated");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const unpublish = async () => {
    await api.post(`/distributions/${id}/unpublish`);
    toast.success("Unpublished");
    load();
  };

  if (!dist) return <div className="eyebrow animate-pulse">Loading…</div>;

  const subPoolData = [
    { name: "Revenue", value: dist.sub_pools.revenue },
    { name: "Capital", value: dist.sub_pools.capital },
    { name: "Tenure", value: dist.sub_pools.tenure },
    { name: "Leadership", value: dist.sub_pools.leadership },
    { name: "Founder", value: dist.sub_pools.founder },
  ];

  const topRows = dist.rows.slice(0, 10).map((r) => ({ name: r.name.split(" ")[0], amount: r.total_share }));

  const isPublished = dist.status === "published";

  return (
    <div className="anim-fade-up">
      <Link to="/admin/distributions" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-atf-text2 hover:text-atf-brand mb-6" data-testid="back-link">
        <ChevronLeft size={14} /> Distributions
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <div className="eyebrow mb-2">{dist.phase_name} · {isPublished ? "Published" : "Draft"}</div>
          <h1 className="display-serif text-5xl text-atf-brand">{dist.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {isPublished ? (
            <button onClick={unpublish} data-testid="unpublish-button" className="border border-atf-border px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold flex items-center gap-2 hover:bg-atf-subtle">
              <Undo2 size={14} /> Unpublish
            </button>
          ) : (
            <>
              <button onClick={() => save(false)} disabled={saving} data-testid="simulate-button" className="border border-atf-brand text-atf-brand px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold flex items-center gap-2 hover:bg-atf-subtle disabled:opacity-50">
                <Save size={14} /> Save & Simulate
              </button>
              <button onClick={() => save(true)} disabled={saving} data-testid="publish-button" className="bg-atf-brand text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold flex items-center gap-2 hover:bg-atf-brand2 disabled:opacity-50">
                <Send size={14} /> Publish to Partners
              </button>
            </>
          )}
        </div>
      </div>

      {/* Header form */}
      {!isPublished && (
        <div className="border border-atf-border bg-white p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Year"><input type="number" value={year} onChange={(e) => setYear(e.target.value)} className={INPUT} data-testid="edit-year" /></Field>
          <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} className={INPUT} data-testid="edit-title" /></Field>
          <Field label="Net Profit (₹)"><input type="number" min={0} value={netProfit} onChange={(e) => setNetProfit(e.target.value)} className={INPUT} data-testid="edit-netprofit" /></Field>
        </div>
      )}

      {/* Snapshot strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-atf-border bg-white mb-10">
        <Stat label="Net Profit" value={fmt(dist.net_profit, currency, rate)} />
        <Stat label={`Growth Reserve (${dist.growth_reserve_pct}%)`} value={fmt(dist.growth_reserve_amount, currency, rate)} />
        <Stat label="Distributable Pool" value={fmt(dist.distributable_pool, currency, rate)} highlight />
        <Stat label="Partners" value={dist.rows.length} />
        <Stat label="Phase" value={dist.phase_name} sub={`${dist.weights.revenue}/${dist.weights.capital}/${dist.weights.tenure}/${dist.weights.leadership}/${dist.weights.founder}`} />
      </div>

      {/* Charts */}
      {dist.rows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <div className="border border-atf-border bg-white p-6">
            <div className="eyebrow mb-1">Sub-pool composition</div>
            <h3 className="font-serif text-2xl text-atf-brand mb-4">How the pool was sliced</h3>
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={subPoolData} dataKey="value" innerRadius={60} outerRadius={95} paddingAngle={2}>
                    {subPoolData.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v, currency, rate)} contentStyle={{ background: "#fff", border: "1px solid #E5E4E2", borderRadius: 0, fontFamily: "IBM Plex Mono" }} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border border-atf-border bg-white p-6">
            <div className="eyebrow mb-1">Top 10 partners</div>
            <h3 className="font-serif text-2xl text-atf-brand mb-4">By total share</h3>
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={topRows}>
                  <CartesianGrid stroke="#E5E4E2" vertical={false} />
                  <XAxis dataKey="name" stroke="#545856" fontSize={11} />
                  <YAxis stroke="#545856" fontSize={11} tickFormatter={(v) => fmt(v, currency, rate)} width={80} />
                  <Tooltip formatter={(v) => fmt(v, currency, rate)} contentStyle={{ background: "#fff", border: "1px solid #E5E4E2", borderRadius: 0, fontFamily: "IBM Plex Mono" }} />
                  <Bar dataKey="amount" fill="#245243" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Entry / Result table */}
      <div className="border border-atf-border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="dist-results-table">
          <thead>
            <tr className="border-b border-atf-border bg-atf-subtle/40">
              <Th>Partner</Th>
              <Th align="right">Revenue (₹)</Th>
              <Th align="right">Capital (₹)</Th>
              <Th align="right">Tenure Pts</Th>
              <Th align="right">Lead Pts</Th>
              <Th align="right">Total Share</Th>
              <Th align="right">% of Pool</Th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => {
              const e = ensureEntries[p.partner_id] || {};
              const row = dist.rows.find((r) => r.partner_id === p.partner_id);
              return (
                <tr key={p.partner_id} className="border-b border-atf-border last:border-b-0" data-testid={`row-${p.partner_id}`}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.name}</span>
                      {p.is_founder && <Crown size={12} className="text-atf-gold" />}
                    </div>
                    <div className="text-[11px] text-atf-text2 mt-0.5">{p.position}{p.leadership_role ? ` · ${p.leadership_role}` : ""}</div>
                  </Td>
                  <Td align="right">
                    {isPublished ? (
                      <span className="tabular">{row ? Number(row.revenue_contribution).toLocaleString("en-IN") : "—"}</span>
                    ) : (
                      <input type="number" min={0} value={e.revenue_contribution ?? 0}
                        onChange={(ev) => setEntry(p.partner_id, "revenue_contribution", ev.target.value)}
                        data-testid={`rev-${p.partner_id}`}
                        className="w-32 border border-atf-border bg-white px-2 py-1.5 text-right tabular text-xs focus:outline-none focus:border-atf-brand" />
                    )}
                  </Td>
                  <Td align="right">
                    {isPublished ? (
                      <span className="tabular">{row ? Number(row.capital_contribution).toLocaleString("en-IN") : "—"}</span>
                    ) : (
                      <input type="number" min={0} value={e.capital_contribution ?? 0}
                        onChange={(ev) => setEntry(p.partner_id, "capital_contribution", ev.target.value)}
                        data-testid={`cap-${p.partner_id}`}
                        className="w-32 border border-atf-border bg-white px-2 py-1.5 text-right tabular text-xs focus:outline-none focus:border-atf-brand" />
                    )}
                  </Td>
                  <Td align="right" mono>{row ? row.tenure_points : "—"}</Td>
                  <Td align="right" mono>{row ? row.leadership_points : "—"}</Td>
                  <Td align="right" mono><strong className="text-atf-brand">{row ? fmt(row.total_share, currency, rate) : "—"}</strong></Td>
                  <Td align="right" mono>{row ? formatPct(row.pct_of_pool) : "—"}</Td>
                </tr>
              );
            })}
            {partners.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center">
                <div className="font-serif text-xl text-atf-brand mb-2">No partners yet.</div>
                <Link to="/admin/partners" className="text-xs uppercase tracking-[0.18em] font-semibold text-atf-brand border-b border-atf-brand">Add partners →</Link>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub, highlight }) => (
  <div className={`p-5 border-r border-atf-border last:border-r-0 ${highlight ? "bg-atf-brand text-white" : ""}`}>
    <div className={`eyebrow ${highlight ? "!text-white/70" : ""}`}>{label}</div>
    <div className={`mt-2 font-serif text-2xl ${highlight ? "text-atf-gold" : "text-atf-brand"}`}>{value}</div>
    {sub && <div className={`mt-1 text-[11px] font-mono ${highlight ? "text-white/60" : "text-atf-text2"}`}>{sub}</div>}
  </div>
);
const Field = ({ label, children }) => <div><label className="eyebrow block mb-2">{label}</label>{children}</div>;
const Th = ({ children, align = "left" }) => <th className={`px-4 py-3 eyebrow text-${align}`}>{children}</th>;
const Td = ({ children, align = "left", mono }) => <td className={`px-4 py-3 text-${align} ${mono ? "tabular" : ""}`}>{children}</td>;
const INPUT = "w-full border border-atf-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-atf-brand focus:ring-1 focus:ring-atf-brand";
